import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/supabaseClient';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Search, UserPlus, ArrowRight, Loader2, X, AlertTriangle, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { hasCap } from '@/lib/capabilities';
import { useEffectiveMe } from '@/lib/effectiveMe';
import AssignmentScopeSelector from '@/components/AssignmentScopeSelector';

const LIFECYCLE_LABEL = {
  BOZZA: 'Bozza', ATTIVO: 'Attivo', SOSPESO: 'Sospeso',
  IN_USCITA: 'In uscita', CESSATO: 'Cessato', ARCHIVIATO: 'Archiviato',
};
const LIFECYCLE_TONE = {
  BOZZA: 'border-stone-200 bg-stone-50 text-stone-600',
  ATTIVO: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  SOSPESO: 'border-amber-200 bg-amber-50 text-amber-800',
  IN_USCITA: 'border-amber-200 bg-amber-50 text-amber-800',
  CESSATO: 'border-red-200 bg-red-50 text-red-700',
  ARCHIVIATO: 'border-stone-200 bg-stone-100 text-stone-600',
};

export default function Personale() {
  const me = useEffectiveMe();
  const canCreate = hasCap(me, 'personale.create');
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [q, setQ] = useState('');
  const [company, setCompany] = useState('all');
  const [location, setLocation] = useState('all');
  const [dept, setDept] = useState('all');
  const [status, setStatus] = useState('all');
  const mountedRef = useRef(false);
  const loadVersionRef = useRef(0);

  const load = async () => {
    const loadVersion = ++loadVersionRef.current;
    try {
      const data = await apiFetch('/api/personale');
      if (mountedRef.current && loadVersion === loadVersionRef.current) {
        setRows(data || []);
      }
    } catch (e) {
      if (mountedRef.current && loadVersion === loadVersionRef.current) {
        toast.error(e.message || 'Errore caricamento personale');
        setRows([]);
      }
    } finally {
      if (mountedRef.current && loadVersion === loadVersionRef.current) {
        setLoading(false);
      }
    }
  };
  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
      loadVersionRef.current += 1;
    };
    // The lifecycle owns this initial fetch and invalidates it on route exit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companies = useMemo(() => rows ? [...new Set(rows.map((r) => r.company_name).filter(Boolean))].sort() : [], [rows]);
  const locations = useMemo(() => rows ? [...new Set(rows.map((r) => r.primary_location).filter(Boolean))].sort() : [], [rows]);
  const departments = useMemo(() => rows ? [...new Set(rows.map((r) => r.department).filter(Boolean))].sort() : [], [rows]);
  const filtered = useMemo(() => (rows || []).filter((r) => {
    const ql = q.trim().toLowerCase();
    if (ql && !`${r.display_name || ''} ${r.codice_ros || ''} ${r.email || ''}`.toLowerCase().includes(ql)) return false;
    if (company !== 'all' && r.company_name !== company) return false;
    if (location !== 'all' && r.primary_location !== location) return false;
    if (dept !== 'all' && r.department !== dept) return false;
    if (status !== 'all' && r.lifecycle_status !== status) return false;
    return true;
  }), [rows, q, company, location, dept, status]);
  const hasFilters = Boolean(q.trim()) || company !== 'all' || location !== 'all' || dept !== 'all' || status !== 'all';
  const clearFilters = () => {
    setQ('');
    setCompany('all');
    setLocation('all');
    setDept('all');
    setStatus('all');
  };

  return (
    <div className="space-y-6" data-testid="personale-page">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green mb-2">Personale</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy">Anagrafica</h1>
          <p className="mt-2 text-stone-500 text-sm" data-testid="personale-count">
            {rows === null ? 'Caricamento elenco…' : `${filtered.length} di ${rows.length} persone visibili`}
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setShowNew(true)} className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-hover text-white gap-2" data-testid="personale-new">
            <UserPlus className="w-4 h-4" /> Nuova persona
          </Button>
        )}
      </header>

      <div className="rounded-2xl border border-app-border bg-app-surface p-4" role="search" aria-label="Filtra elenco personale">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-brand-navy">
            <SlidersHorizontal className="h-4 w-4 text-brand-green" />
            Filtra elenco
          </div>
          {hasFilters && (
            <Button type="button" variant="ghost" size="sm" onClick={clearFilters} className="text-brand-blue" data-testid="personale-clear-filters">
              Azzera filtri
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
          <div className="relative md:col-span-2">
            <label htmlFor="personale-search" className="sr-only">Cerca per nome, codice o email</label>
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input id="personale-search" data-testid="personale-search" placeholder="Cerca nome, codice, email…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          <div>
            <label htmlFor="filter-company" className="sr-only">Filtra per azienda</label>
            <select id="filter-company" value={company} onChange={(e) => setCompany(e.target.value)} data-testid="filter-company" className="h-10 w-full min-w-0 rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm text-app-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200">
              <option value="all">Tutte le aziende</option>
              {companies.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="filter-location" className="sr-only">Filtra per locale</label>
            <select id="filter-location" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="filter-location" className="h-10 w-full min-w-0 rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm text-app-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200">
              <option value="all">Tutti i locali</option>
              {locations.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="filter-dept" className="sr-only">Filtra per reparto</label>
            <select id="filter-dept" value={dept} onChange={(e) => setDept(e.target.value)} data-testid="filter-dept" className="h-10 w-full min-w-0 rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm text-app-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200">
              <option value="all">Tutti i reparti</option>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="md:col-start-5">
            <label htmlFor="filter-status" className="sr-only">Filtra per stato</label>
            <select id="filter-status" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status" className="h-10 w-full min-w-0 rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm text-app-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200">
              <option value="all">Tutti gli stati</option>
              {Object.entries(LIFECYCLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading && <div className="flex items-center gap-2 text-stone-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>}

      {!loading && filtered.length === 0 && (
        <div className="rounded-2xl border border-dashed border-stone-300 bg-app-surface p-10 text-center text-stone-500 text-sm">Nessuna persona nel tuo scope con questi filtri.</div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {filtered.map((p) => (
          <Link key={p.legacy_id} to={`/personale/${p.legacy_id}`} data-testid={`personale-row-${p.legacy_id}`}
            className="group rounded-2xl border border-app-border bg-app-surface p-4 transition-colors hover:border-brand-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green focus-visible:ring-offset-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate font-display font-semibold text-brand-navy">{p.display_name}</div>
                <div className="mt-0.5 text-xs text-stone-500">{p.codice_ros || 'Codice non disponibile'}</div>
              </div>
              <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-semibold uppercase ${LIFECYCLE_TONE[p.lifecycle_status] || LIFECYCLE_TONE.BOZZA}`}
                data-testid={`lifecycle-${p.legacy_id}`}>{LIFECYCLE_LABEL[p.lifecycle_status] || p.lifecycle_status}</span>
            </div>
            <div className="mt-3 text-sm text-stone-600 space-y-0.5">
              <div><span className="text-stone-400">Mansione:</span> {p.job_title || '—'}</div>
              <div><span className="text-stone-400">Reparto:</span> {p.department || '—'}</div>
              <div><span className="text-stone-400">Azienda:</span> {p.company_name || '—'}</div>
              <div>
                <span className="text-stone-400">Locale:</span> {p.primary_location || '—'}
                {p.assignments_count > 1 && (
                  <span className="ml-2 inline-block text-xs px-1.5 py-0.5 rounded bg-brand-blue/10 text-brand-blue font-semibold" data-testid={`multi-assign-${p.legacy_id}`}>
                    +{p.assignments_count - 1}
                  </span>
                )}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-end text-xs font-semibold text-brand-green transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-visible:opacity-100">
              Apri scheda <ArrowRight className="w-3 h-3 ml-1" />
            </div>
          </Link>
        ))}
      </div>

      {showNew && <NewPersonModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewPersonModal({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    display_name: '', first_name: '', last_name: '',
    email_personal: '', phone: '',
    company_id: '', location_id: '', department_id: '', mansione: '',
    confirm_duplicate: false,
  });
  const [dedup, setDedup] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runDedup = async () => {
    try {
      const params = new URLSearchParams();
      if (form.email_personal) params.set('email', form.email_personal);
      if (form.display_name) params.set('display_name', form.display_name);
      const res = await apiFetch(`/api/personale/dedup/check?${params.toString()}`);
      setDedup(res); setStep(2);
    } catch (e) { toast.error(e.message); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const { mansione, ...person } = form;
      const payload = { ...person, job_title: mansione || null };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
      const res = await apiFetch('/api/personale', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      toast.success(`Persona ${res.legacy_id} creata (${res.lifecycle_status})`);
      onCreated();
    } catch (e) {
      if (e.status === 409) {
        toast.error('Duplicato — conferma di voler procedere');
        setStep(2);
      } else { toast.error(e.message || 'Errore'); }
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="new-person-modal" role="dialog" aria-modal="true" aria-labelledby="new-person-title" onClick={onClose} onKeyDown={(e) => e.key === 'Escape' && onClose()}>
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-app-border bg-app-surface p-5 shadow-sm sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div id="new-person-title" className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green">Nuova persona</div>
            <div className="text-sm text-stone-500">Passo {step} di 2</div>
          </div>
          <button onClick={onClose} aria-label="Chiudi nuova persona" data-testid="modal-close" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-stone-400 hover:bg-stone-50 hover:text-brand-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200"><X className="w-5 h-5" /></button>
        </div>
        {step === 1 && (
          <div className="space-y-3">
            <div><Label htmlFor="np-display-name">Nome visualizzato *</Label><Input id="np-display-name" value={form.display_name} onChange={(e) => set('display_name', e.target.value)} data-testid="np-display-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label htmlFor="np-first-name">Nome</Label><Input id="np-first-name" value={form.first_name} onChange={(e) => set('first_name', e.target.value)} data-testid="np-first-name" /></div>
              <div><Label htmlFor="np-last-name">Cognome</Label><Input id="np-last-name" value={form.last_name} onChange={(e) => set('last_name', e.target.value)} data-testid="np-last-name" /></div>
            </div>
            <div><Label htmlFor="np-email">Email personale</Label><Input id="np-email" value={form.email_personal} onChange={(e) => set('email_personal', e.target.value)} data-testid="np-email" /></div>
            <div><Label htmlFor="np-phone">Telefono</Label><Input id="np-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="np-phone" /></div>
            <Button onClick={runDedup} disabled={!form.display_name} className="bg-brand-green hover:bg-brand-green-hover text-white w-full" data-testid="np-next-1">Avanti · verifica duplicati</Button>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-3">
            {dedup?.matches?.length > 0 ? (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="dedup-warning">
                <div className="flex items-center gap-2 font-semibold mb-1"><AlertTriangle className="w-4 h-4" /> Possibili duplicati</div>
                {dedup.matches.map((m) => <div key={m.id}>· {m.display_name} ({m.match})</div>)}
                <label className="flex items-center gap-2 mt-3">
                  <input type="checkbox" checked={form.confirm_duplicate} onChange={(e) => set('confirm_duplicate', e.target.checked)} data-testid="np-confirm-dup" />
                  Sono sicuro, è una nuova persona
                </label>
              </div>
            ) : <div className="text-sm text-stone-500">Nessun duplicato trovato.</div>}
            <div className="rounded-xl border border-app-border bg-stone-50/70 p-3">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-stone-500">Assegnazione iniziale</div>
              <AssignmentScopeSelector
                value={form}
                onChange={(patch) => setForm((current) => ({ ...current, ...patch }))}
                optionalLocation
                optionalMansione
                testidPrefix="np"
              />
              <p className="mt-2 text-xs text-stone-500">Senza locale la persona viene creata come Bozza e potrà essere assegnata in seguito.</p>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">Indietro</Button>
              <Button onClick={submit} disabled={busy || !form.company_id || (dedup?.matches?.length > 0 && !form.confirm_duplicate)} className="flex-1 bg-brand-green hover:bg-brand-green-hover text-white" data-testid="np-submit">
                {busy ? 'Salvo…' : 'Crea persona'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
